import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useUserEmojiList } from './useEmojiPacks';
import type { EmojiPack } from './useEmojiPacks';
import { useToast } from './useToast';

export function useEmojiListManager() {
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { data: emojiList } = useUserEmojiList(user?.pubkey);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const packRefs = emojiList?.packRefs ?? [];

  function isPackAdded(pack: EmojiPack): boolean {
    const ref = `30030:${pack.pubkey}:${pack.dTag}`;
    return packRefs.includes(ref);
  }

  async function addPack(pack: EmojiPack) {
    if (!user) {
      toast({ title: 'Login required', description: 'Please log in to manage your emoji packs.', variant: 'destructive' });
      return;
    }

    const ref = `30030:${pack.pubkey}:${pack.dTag}`;
    if (packRefs.includes(ref)) return;

    const existingEvent = emojiList?.event;
    const existingTags = existingEvent?.tags ?? [];

    // Filter out old "a" tags that point to 30030 packs (keep emoji tags and others)
    const otherTags = existingTags.filter(([name]) => name !== 'a' || !existingTags.some(([n, v]) => n === 'a' && v?.startsWith('30030:')));
    const existingATags = existingTags.filter(([name, value]) => name === 'a' && value?.startsWith('30030:'));

    const newTags = [
      ...existingTags.filter(([name, value]) => !(name === 'a' && value?.startsWith('30030:'))),
      ...existingATags,
      ['a', ref],
    ];

    await publish({
      kind: 10030,
      content: existingEvent?.content ?? '',
      tags: newTags,
    });

    queryClient.invalidateQueries({ queryKey: ['emoji-list', user.pubkey] });
    toast({ title: '✨ Pack added!', description: `"${pack.title}" has been added to your profile.` });
  }

  async function removePack(pack: EmojiPack) {
    if (!user) return;

    const ref = `30030:${pack.pubkey}:${pack.dTag}`;
    const existingEvent = emojiList?.event;
    const existingTags = existingEvent?.tags ?? [];

    const newTags = existingTags.filter(([name, value]) => !(name === 'a' && value === ref));

    await publish({
      kind: 10030,
      content: existingEvent?.content ?? '',
      tags: newTags,
    });

    queryClient.invalidateQueries({ queryKey: ['emoji-list', user.pubkey] });
    toast({ title: 'Pack removed', description: `"${pack.title}" has been removed from your profile.` });
  }

  return { packRefs, isPackAdded, addPack, removePack };
}
